import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";
import { normalizeLedgerNameList } from "../utils/ledgerUtils";

/**
 * Bulk-search entities by uploading an Excel file.
 * POST /api/Onboarding/bulk-search-entities
 * multipart/form-data: file (.xlsx)
 *
 * Response has the same record shape as GET /api/Onboarding/get-entities,
 * so we reuse the exact same mapRecord transform.
 *
 * @param {File} file
 * @returns {{ success: boolean, data: object[], totalRecords: number, message?: string }}
 */

// ── Same mapper used by entityListActions.js ──────────────────────────────────
const normalizeDept = (v) => {
  if (!v) return "";
  const m = {
    operation: "Operation",
    operations: "Operation",
    sales: "Sales",
    purchase: "Purchase",
    marketing: "Marketing",
    hr: "HR",
    "human resources": "HR",
    it: "IT",
    finance: "Finance",
    compliance: "Compliance",
  };
  return m[v.toLowerCase().trim()] ?? v;
};

const mapRecord = (r) => {
  const prop = r.proprietorshipDetails || {};
  const partner = r.partnershipDetails || {};
  const llp = r.llpDetails || {};
  const pvt = r.privatePublicLimitedCompanyDetails || {};
  const trust = r.trustDetails || {};
  const msme = r.msmeDetails || {};
  const utility = r.utilityBillDetails || {};
  const bank = r.bankDetails || {};
  const contact = r.contactPersonDetails || {};

  return {
    entity: {
      entityPrimaryId: r.entityPrimaryId,
      entityId: r.entityId,
      entityName: r.entityName,
      entityCategory: r.entityCategory,
      lobType: r.entityLOBType,
      entityLOBType: r.entityLOBType,
      tradeName: r.tradeName,
      parentCode: r.parentCode,
      parentId: r.parentId,
      parentCompanyName: r.parentName,
      aaDepartmentFunction: normalizeDept(r.aaDepartmentFunction),
      emailAddress: r.emailAddress,
      spocEmail: r.spoC_EmailAddress,
      mobileNumber: contact.mobileNumber,
      contactPersonName: contact.contactPersonName,
      pinCode: r.pinCode,
      city: r.city,
      state: r.state || r.State,
      address: r.address,
      natureOfBusiness: r.natureOfBusiness,
      isInvoiceRequired: r.invoiceRequired,
      entityType: r.entityType,
      requireKYC: r.requireKYC,
      isResendLink: r.isResendLink,
      isVerified: r.isVerified,
      isDeficiency: r.isDeficiency || false,
      isDocumentUploaded: r.isDocumentUploaded,
      isSaveAsDraft: r.isSaveAsDraft,
      isLedger: r.isLedger ?? null,
      isLedgerUploaded: r.isLedgerUploaded ?? false,
      createdOn: r.createdOn,
      kycCreatedOn: r.kycCreatedOn,
      folderNo: r.folderNo || null,
      // Bank
      hasBankDetails: bank.provideBankDetails ? "yes" : "no",
      bankName: bank.bankName,
      accountNumber: bank.accountNumber,
      ifscCode: bank.ifscCode,
      accountType: bank.accountType,
      branchName: bank.branchName,
      beneficiaryName: bank.beneficiaryName,
      passbookOrCancelledChequePath: bank.passbookOrCancelledChequePath,
      isPassbookOrCancelledChequeDef: bank.isPassbookOrCancelledChequeDef,
      uploadSignedFormPath: bank.uploadSignedFormPath,
      isSignedFormDef: bank.isSignedFormDef,
      // MSME
      isRegisteredUnderMSME: msme.isRegisteredUnderMSME,
      msmeRegistrationNumber: msme.msmeRegistrationNumber,
      msmeCertificatePath: msme.msmeCertificatePath,
      isMSMECertificateDef: msme.isMSMECertificateDef,
      // Utility
      utilityType: utility.utilityType,
      utilityBillDocumentPath: utility.utilityBillDocumentPath,
      isUtilityBillDocumentDef: utility.isUtilityBillDocumentDef,
      // Proprietorship
      aadharCardPath:
        prop.aadharCardPath ||
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        r.aadharCardPath ||
        "",
      aadharCardBackPath:
        r.aadharCardBackPath ||
        prop.aadharCardBackPath ||
        partner.aadharCardBackPath ||
        llp.aadharCardBackPath ||
        "",
      panCardPath: prop.panCardPath,
      // Partnership / LLP
      partnershipDeedPath:
        partner.partnershipDeedPath || llp.partnershipDeedPath,
      aadharOfBothPartnersPath:
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        prop.aadharCardPath ||
        r.aadharCardPath ||
        "",
      aadharOfBothPartnersPathSecond:
        partner.aadharOfBothPartnersPathSecond ||
        llp.aadharOfBothPartnersPathSecond,
      isAadharOfBothPartnersDefSecond:
        partner.isAadharOfBothPartnersDefSecond ||
        llp.isAadharOfBothPartnersDefSecond,
      uploadLLPCertificatePath: llp.uploadLLPCertificatePath,
      isLLPCertificateDef: llp.isLLPCertificateDef,
      // PVT/Ltd
      certificateOfIncorporationPath: pvt.certificateOfIncorporationPath,
      directorPANPath: pvt.directorPANPath,
      directorAadharPath: pvt.directorAadharPath,
      // Trust
      trustDeedPath: trust.trustDeedPath,
      // GST
      isGSTRegistered: r.isGSTRegistered,
      gstList: Array.isArray(r.gstList) ? r.gstList : [],
      // Ledger
      ledgerList: r.ledgerList || [],
      ledgerNameList: normalizeLedgerNameList(r.ledgerNameList),
      // Deficiency map (built same way as entityListActions)
      deficiencyMap: {},
    },
    documents: buildDocuments(
      r,
      prop,
      partner,
      llp,
      pvt,
      trust,
      msme,
      utility,
      bank,
    ),
  };
};

// Build the same documents array structure used throughout the app
const buildDocuments = (
  r,
  prop,
  partner,
  llp,
  pvt,
  trust,
  msme,
  utility,
  bank,
) => {
  const docs = [];
  const push = (key, label, isUploaded, path) => {
    docs.push({
      key,
      label,
      files: [{ isUploaded: !!isUploaded, filePath: path || "" }],
    });
  };

  const frontAadharPath =
    prop.aadharCardPath ||
    partner.aadharOfBothPartnersPath ||
    llp.aadharOfBothPartnersPath ||
    r.aadharCardPath ||
    "";
  const frontAadharUploaded =
    prop.aadharCardUploaded ||
    prop.isAadharCard ||
    partner.aadharOfBothPartnersUploaded ||
    llp.aadharOfBothPartnersUploaded ||
    !!frontAadharPath;

  push("doc_aadhar", "Aadhar Card", frontAadharUploaded, frontAadharPath);

  const backAadharPath =
    r.aadharCardBackPath ||
    prop.aadharCardBackPath ||
    partner.aadharCardBackPath ||
    llp.aadharCardBackPath ||
    "";
  if (backAadharPath && backAadharPath !== "string") {
    push("aadharCardBackPath", "Aadhar Card (Back)", true, backAadharPath);
  }

  push("doc_pan", "PAN Card", prop.panCardUploaded, prop.panCardPath);
  push(
    "doc_partnershipDeed",
    "Partnership Deed",
    partner.partnershipDeedUploaded || llp.partnershipDeedUploaded,
    partner.partnershipDeedPath || llp.partnershipDeedPath,
  );
  push(
    "doc_aadharBoth",
    "Aadhar of Both Partners",
    frontAadharUploaded,
    frontAadharPath,
  );
  push(
    "doc_aadharBothSecondary",
    "Aadhar of Secondary Partner",
    partner.aadharOfBothPartnersUploadedSecond ||
      llp.aadharOfBothPartnersUploadedSecond,
    partner.aadharOfBothPartnersPathSecond ||
      llp.aadharOfBothPartnersPathSecond,
  );
  push(
    "doc_llpCertificate",
    "LLP Certificate",
    llp.llpCertificateUploaded,
    llp.uploadLLPCertificatePath,
  );
  push(
    "doc_coi",
    "Certificate of Incorporation",
    pvt.certificateOfIncorporationUploaded,
    pvt.certificateOfIncorporationPath,
  );
  push(
    "doc_directorPan",
    "Director PAN",
    pvt.directorPanUploaded,
    pvt.directorPANPath,
  );
  push(
    "doc_directorAadhar",
    "Director Aadhar",
    pvt.directorAadharUploaded,
    pvt.directorAadharPath,
  );
  push(
    "doc_trustDeed",
    "Trust Deed",
    trust.trustDeedUploaded,
    trust.trustDeedPath,
  );
  push(
    "doc_panTrust",
    "PAN of Trust",
    trust.trustPanUploaded,
    trust.trustPanPath,
  );
  push(
    "doc_panTrustee",
    "PAN of Trustee",
    trust.trusteePanUploaded,
    trust.trusteePanPath,
  );
  push(
    "doc_aadharTrustee",
    "Aadhar of Trustee",
    trust.trusteeAadharUploaded,
    trust.trusteeAadharPath,
  );
  push(
    "doc_msme_certificate",
    "MSME Certificate",
    msme.msmeCertificateUploaded,
    msme.msmeCertificatePath,
  );
  push(
    "doc_utility_bill",
    "Utility Bill",
    utility.utilityBillDocumentUploaded,
    utility.utilityBillDocumentPath,
  );
  push(
    "doc_bank_proof",
    "Bank Proof",
    bank.passbookOrCancelledChequeUploaded,
    bank.passbookOrCancelledChequePath,
  );
  push(
    "third_party_file",
    "Signed Form",
    bank.signedFormUploaded,
    bank.uploadSignedFormPath,
  );

  // GST entries
  if (Array.isArray(r.gstList)) {
    r.gstList.forEach((g, i) => {
      push(
        `gst_file_${i}`,
        `GST Certificate ${i + 1}`,
        g.isGST,
        g.gstCertificatePath,
      );
    });
  }

  return docs;
};

// ── Action ────────────────────────────────────────────────────────────────────
export const uploadExcelForBulkSearchAction = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post(
      ATP_API_URL.BULK_SEARCH_ALL_LIST,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    if (response?.status === 200 && response.data) {
      const rawList = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      const totalRecords = response.data?.totalRecords ?? rawList.length;

      const pagination = {
        currentPage: response.data?.currentPage ?? 1,
        totalPages: response.data?.totalPages ?? 1,
        totalItems: totalRecords,
        pageSize: response.data?.pageSize ?? rawList.length,
      };

      return {
        success: true,
        data: rawList.map(mapRecord),
        totalRecords,
        pagination,
        message: response.data?.validationMessage || "",
      };
    }

    return {
      success: false,
      data: [],
      totalRecords: 0,
      message: "Bulk search failed",
    };
  } catch (error) {
    console.error(
      "[BulkSearchEntities] error:",
      error?.response?.data || error.message,
    );
    return {
      success: false,
      data: [],
      totalRecords: 0,
      message:
        error?.response?.data?.message || error.message || "Bulk search failed",
    };
  }
};

// Keep default export for backward compatibility
export default uploadExcelForBulkSearchAction;
